import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Wrench, AlertCircle, Loader2, Building2 } from 'lucide-react';

const CompanyLoginPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { fetchCompanyBySlug, setCompanyContext } = useBranding();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [companyNotFound, setCompanyNotFound] = useState(false);

  useEffect(() => {
    const loadCompany = async () => {
      if (!slug) {
        setCompanyNotFound(true);
        setLoadingCompany(false);
        return;
      }
      
      const company = await fetchCompanyBySlug(slug);
      if (company) {
        setCompanyInfo(company);
        // Apply company branding temporarily for login page
        setCompanyContext(company);
      } else {
        setCompanyNotFound(true);
      }
      setLoadingCompany(false);
    };
    
    loadCompany();
  }, [slug, fetchCompanyBySlug, setCompanyContext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, slug);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Show loading state while fetching company
  if (loadingCompany) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Company not found
  if (companyNotFound) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase text-white mb-2">
            Company Not Found
          </h1>
          <p className="text-slate-400 mb-6">
            The company portal you're looking for doesn't exist or has been suspended.
          </p>
          <Link to="/login">
            <Button className="btn-primary">
              Go to Main Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const primaryColor = companyInfo?.primary_color || '#F97316';

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="company-login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Company Logo and Title */}
          <div className="text-left">
            <div className="flex items-center gap-3 mb-6">
              {companyInfo?.logo_url ? (
                <img 
                  src={companyInfo.logo_url} 
                  alt={companyInfo.name} 
                  className="w-12 h-12 rounded-sm object-contain"
                />
              ) : (
                <div 
                  className="w-12 h-12 rounded-sm flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: primaryColor }}
                >
                  {companyInfo?.name?.charAt(0)?.toUpperCase() || 'C'}
                </div>
              )}
              <span className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
                {companyInfo?.name || 'Company Portal'}
              </span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Welcome Back
            </h1>
            <p className="mt-2 text-slate-400">
              Sign in to access your {companyInfo?.name} workspace
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/50 rounded-sm text-red-400" data-testid="login-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="company-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-400 uppercase text-xs tracking-wider">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="input-industrial w-full"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-400 uppercase text-xs tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="input-industrial w-full"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 text-white font-medium py-3 rounded-sm transition-colors"
              style={{ backgroundColor: primaryColor }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Link to main login */}
          <p className="text-center text-slate-400 text-sm">
            Not a {companyInfo?.name} user?{' '}
            <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium">
              Use main login
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Branded panel */}
      <div className="hidden lg:block lg:flex-1 relative" style={{ backgroundColor: primaryColor }}>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-transparent z-10" />
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="absolute bottom-12 left-12 z-20 max-w-md">
          <div 
            className="w-20 h-20 rounded-sm flex items-center justify-center text-white font-bold text-3xl mb-6"
            style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
          >
            {companyInfo?.name?.charAt(0)?.toUpperCase() || 'C'}
          </div>
          <p className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-tight text-white">
            {companyInfo?.name}
          </p>
          <p className="mt-2 text-white/80">
            Your dedicated maintenance management portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyLoginPage;
